// Portal split (task brief): a COPY of apps-future/console/src/lib/formatters.ts,
// not a shared import -- see this app's scripts/generate-grpc-web.sh header
// for why this app carries its own copy of the generated ConsoleService
// client (and everything keyed to it, including this file). Only the
// labels this app's pages actually use (billing/license/usage formatters)
// are load-bearing here; the rest (enrollment/connectivity/instance-
// oriented labels) are operator-only concepts this app never renders, kept
// only to avoid a partial, drift-prone fork of the file. If this ever
// drifts from console's copy in a way that matters, promote both to a
// shared package -- see the task's final report for this decision.
import * as pb from '@/generated/console_pb'
import type { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'

const STALE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes -- no proto-defined
// threshold exists for "stale handshake" (InstanceHeartbeatResponse only
// carries grace_period_seconds, which is a per-response, per-license
// value returned AFTER a heartbeat succeeds, not a fixed fleet-wide
// staleness threshold usable from ListInstances' last_handshake_at alone).
// 5 minutes matches D5's "~24h refresh, grace period on missed check-in"
// order of magnitude for a *visual* staleness flag in a list view, not an
// enforcement decision -- enforcement is server-side per ADR-118 D6.

export function timestampToDate(ts: Timestamp | undefined): Date | null {
  if (!ts) return null
  const seconds = ts.getSeconds()
  if (!seconds) return null
  return new Date(seconds * 1000 + ts.getNanos() / 1e6)
}

export function formatRelativeAge(ts: Timestamp | undefined): string {
  const date = timestampToDate(ts)
  if (!date) return 'never'
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

export function isHandshakeStale(ts: Timestamp | undefined): boolean {
  const date = timestampToDate(ts)
  if (!date) return true
  return Date.now() - date.getTime() > STALE_THRESHOLD_MS
}

export function enrollmentStatusLabel(s: pb.InstanceEnrollmentStatus): string {
  switch (s) {
    case pb.InstanceEnrollmentStatus.INSTANCE_ENROLLMENT_STATUS_PENDING:
      return 'Pending'
    case pb.InstanceEnrollmentStatus.INSTANCE_ENROLLMENT_STATUS_ACTIVE:
      return 'Active'
    case pb.InstanceEnrollmentStatus.INSTANCE_ENROLLMENT_STATUS_SUSPENDED:
      return 'Suspended'
    case pb.InstanceEnrollmentStatus.INSTANCE_ENROLLMENT_STATUS_DECOMMISSIONED:
      return 'Decommissioned'
    default:
      return 'Unspecified'
  }
}

export function deploymentTypeLabel(t: pb.DeploymentType): string {
  switch (t) {
    case pb.DeploymentType.DEPLOYMENT_TYPE_HOSTED:
      return 'Hosted'
    case pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_CONNECTED:
      return 'Private (connected)'
    case pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_OFFLINE:
      return 'Private (offline)'
    default:
      return 'Unspecified'
  }
}

export function licenseStatusLabel(s: pb.LicenseStatus): string {
  switch (s) {
    case pb.LicenseStatus.LICENSE_STATUS_ACTIVE:
      return 'Active'
    case pb.LicenseStatus.LICENSE_STATUS_SUSPENDED:
      return 'Suspended'
    case pb.LicenseStatus.LICENSE_STATUS_REVOKED:
      return 'Revoked'
    default:
      return 'Unspecified'
  }
}

export function licenseTierLabel(t: pb.LicenseTier): string {
  switch (t) {
    case pb.LicenseTier.LICENSE_TIER_TIER1:
      return 'Tier 1'
    case pb.LicenseTier.LICENSE_TIER_TIER2:
      return 'Tier 2'
    case pb.LicenseTier.LICENSE_TIER_TIER3:
      return 'Tier 3'
    case pb.LicenseTier.LICENSE_TIER_TIER4:
      return 'Tier 4'
    case pb.LicenseTier.LICENSE_TIER_INTERNAL:
      return 'Internal'
    case pb.LicenseTier.LICENSE_TIER_CUSTOM:
      return 'Custom'
    default:
      return 'Unspecified'
  }
}

export function billingStateLabel(s: pb.BillingState): string {
  switch (s) {
    case pb.BillingState.BILLING_STATE_ACTIVE:
      return 'Active'
    case pb.BillingState.BILLING_STATE_DELAYED:
      return 'Delayed'
    case pb.BillingState.BILLING_STATE_UNMETERED:
      return 'Unmetered'
    case pb.BillingState.BILLING_STATE_PROMOTIONAL:
      return 'Promotional'
    case pb.BillingState.BILLING_STATE_PARTNER:
      return 'Partner'
    case pb.BillingState.BILLING_STATE_COMP:
      return 'Comp'
    default:
      return 'Unspecified'
  }
}

export function dataClassificationLabel(c: pb.DataClassification): string {
  switch (c) {
    case pb.DataClassification.DATA_CLASSIFICATION_OPERATIONAL:
      return 'Operational'
    case pb.DataClassification.DATA_CLASSIFICATION_BILLING:
      return 'Billing'
    case pb.DataClassification.DATA_CLASSIFICATION_DIAGNOSTIC:
      return 'Diagnostic'
    default:
      return 'Unspecified'
  }
}

export function formatTimestamp(ts: Timestamp | undefined): string {
  const date = timestampToDate(ts)
  if (!date) return '—'
  return date.toLocaleString()
}

export function orgScopeLabel(s: pb.OrgScope): string {
  switch (s) {
    case pb.OrgScope.ORG_SCOPE_WILDCARD:
      return 'Wildcard'
    case pb.OrgScope.ORG_SCOPE_ACCOUNT_BOUND:
      return 'Account-bound'
    default:
      return 'Unspecified'
  }
}

// connectivityModeLabel/connectivityBadgeLabel are the ADR-181 D5 P1 rider's
// connectivity/deployment_type badge -- the signal an operator needs to know
// whether a remote SuspendLicense call is even a live lever for a given
// license/instance (private_offline has no remote kill-switch, per ADR-178
// D6: "offline instances only ever expire, they cannot be remotely
// suspended").
export function connectivityModeLabel(m: pb.ConnectivityMode): string {
  switch (m) {
    case pb.ConnectivityMode.CONNECTIVITY_MODE_CONNECTED:
      return 'Connected'
    case pb.ConnectivityMode.CONNECTIVITY_MODE_OFFLINE:
      return 'Offline'
    default:
      return 'Unspecified'
  }
}

// connectivityBadgeLabel is the short combined deployment_type +
// connectivity_mode badge text shown next to a license/account/instance
// throughout the dashboard (D5 point 1: "visible everywhere an account or
// instance is shown").
export function connectivityBadgeLabel(deploymentType: pb.DeploymentType): string {
  switch (deploymentType) {
    case pb.DeploymentType.DEPLOYMENT_TYPE_HOSTED:
      return 'Hosted'
    case pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_CONNECTED:
      return 'Private, connected'
    case pb.DeploymentType.DEPLOYMENT_TYPE_PRIVATE_OFFLINE:
      return 'Air-gapped'
    default:
      return 'Unspecified'
  }
}

// formatExpiryCountdown renders the D5 expiry-countdown signal from an
// alert's days_until_expiry: "N days" for a positive count, "today" for
// zero, and a distinct "no expiry" for licenses with no expires_at at all
// (Tier 4/contract licenses -- see ADR-181 D5's fully-disconnected tier,
// which has no fixed term). hasExpiresAt distinguishes "expiry is today"
// (days=0, hasExpiresAt=true) from "no expiry set" (days=0,
// hasExpiresAt=false) -- both would otherwise read as "0 days," which
// hides exactly the open-ended-vs-imminent distinction an operator needs.
export function formatExpiryCountdown(daysUntilExpiry: number, hasExpiresAt: boolean): string {
  if (!hasExpiresAt) return 'No fixed expiry'
  if (daysUntilExpiry <= 0) return 'Expires today'
  if (daysUntilExpiry === 1) return '1 day remaining'
  return `${daysUntilExpiry} days remaining`
}
